import { PitchShell } from "@/components/pitch-practice/PitchShell";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Mic, Square, Loader2, Play, ClipboardCheck, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { GrantAvatar } from "@/components/pitch-practice/GrantPersona";
import { finishSession, founderTurn, getSession, speak } from "@/lib/pitch-practice/pitch.functions";
import type { Turn } from "@/lib/pitch-practice/grant";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/accelerator/pitch-practice/$id")({
  head: () => ({
    meta: [
      { title: "Pitch Room — Live with Grant Rogers" },
      { name: "description", content: "Deliver your pitch out loud and answer Grant Rogers' follow-up questions." },
      { property: "og:title", content: "Pitch Room — Live with Grant Rogers" },
      { property: "og:description", content: "Voice pitch practice with a dual-use VC partner." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: () => (
    <PitchShell>
      <PitchRoom />
    </PitchShell>
  ),
});

type Phase = "ready" | "speaking" | "listening" | "recording" | "thinking" | "scoring";

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(",")[1] ?? "");
    r.onerror = reject;
    r.readAsDataURL(blob);
  });
}

function PitchRoom() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const fetchSession = useServerFn(getSession);
  const speakFn = useServerFn(speak);
  const turnFn = useServerFn(founderTurn);
  const finishFn = useServerFn(finishSession);

  const session = useQuery({ queryKey: ["session", id], queryFn: () => fetchSession({ data: { id } }) });
  const [turns, setTurns] = useState<Turn[]>([]);
  const [phase, setPhase] = useState<Phase>("ready");
  const [seconds, setSeconds] = useState(0);
  const recorder = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const audioEl = useRef<HTMLAudioElement | null>(null);
  const bottom = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (session.data) {
      setTurns(session.data.transcript);
      if (session.data.status === "scored") navigate({ to: "/accelerator/pitch-practice/scorecard/$id", params: { id } });
    }
  }, [session.data, id, navigate]);

  // Effects must return nothing or a cleanup function — never another value,
  // or React crashes with "is not a function" when the page updates or closes.
  useEffect(() => {
    bottom.current?.scrollIntoView({ behavior: "smooth" });
  }, [turns, phase]);

  useEffect(() => {
    if (phase !== "recording") return;
    setSeconds(0);
    const t = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [phase]);

  useEffect(() => {
    return () => {
      audioEl.current?.pause();
    };
  }, []);

  async function say(text: string) {
    setPhase("speaking");
    try {
      const { audio } = await speakFn({ data: { text } });
      await new Promise<void>((resolve) => {
        const el = new Audio(`data:audio/wav;base64,${audio}`);
        audioEl.current = el;
        el.onended = () => resolve();
        el.onerror = () => resolve();
        el.play().catch(() => resolve());
      });
    } catch (e: any) {
      toast.error(e.message ?? "Couldn't play Grant's voice — read his question above.");
    }
    setPhase("listening");
  }

  function skipAudio() {
    if (audioEl.current) {
      audioEl.current.pause();
      audioEl.current.onended?.(new Event("ended"));
    }
  }

  async function startRecording() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "audio/mp4";
      const rec = new MediaRecorder(stream, { mimeType: mime });
      chunks.current = [];
      rec.ondataavailable = (e) => e.data.size && chunks.current.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunks.current, { type: mime });
        await submit(blob, mime);
      };
      rec.start();
      recorder.current = rec;
      setPhase("recording");
    } catch {
      toast.error("Microphone access is needed to pitch. Allow it in your browser settings and try again.");
    }
  }

  function stopRecording() {
    recorder.current?.stop();
    setPhase("thinking");
  }

  async function submit(blob: Blob, mime: string) {
    setPhase("thinking");
    try {
      const audio = await blobToBase64(blob);
      const res = await turnFn({ data: { id, audio, mime } });
      const now = new Date().toISOString();
      setTurns((t) => [...t, { role: "founder", text: res.said, at: now }, { role: "grant", text: res.reply, at: now }]);
      await say(res.reply);
      if (res.ended) await score();
    } catch (e: any) {
      toast.error(e.message ?? "Something went wrong");
      setPhase("listening");
    }
  }

  async function score() {
    setPhase("scoring");
    try {
      await finishFn({ data: { id } });
      navigate({ to: "/accelerator/pitch-practice/scorecard/$id", params: { id } });
    } catch (e: any) {
      toast.error(e.message ?? "Couldn't build the scorecard");
      setPhase("listening");
    }
  }

  if (session.isLoading) return <div className="grid min-h-[70vh] place-items-center bg-background text-muted-foreground">Loading…</div>;
  if (session.error || !session.data)
    return (
      <div className="grid min-h-[70vh] place-items-center bg-background text-center">
        <div>
          <p className="text-destructive">{(session.error as Error)?.message ?? "Session not found"}</p>
          <Button asChild variant="outline" className="mt-4">
            <Link to="/accelerator/pitch-practice">Back</Link>
          </Button>
        </div>
      </div>
    );

  const s = session.data;
  const founderCount = turns.filter((t) => t.role === "founder").length;
  const statusLabel: Record<Phase, string> = {
    ready: "Ready when you are",
    speaking: "Grant is speaking",
    listening: founderCount === 0 ? "Your floor — deliver the pitch" : "Your turn to answer",
    recording: "Recording",
    thinking: "Grant is thinking",
    scoring: "Writing your scorecard",
  };

  return (
    <div className="flex min-h-[70vh] flex-col bg-background">
      <header className="border-b border-border">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-3 px-4 py-3">
          <Button asChild variant="ghost" size="sm">
            <Link to="/accelerator/pitch-practice">
              <ArrowLeft /> Exit
            </Link>
          </Button>
          <div className="min-w-0 text-center">
            <p className="truncate text-sm font-semibold">{s.startup_name}</p>
            <p className="text-[0.7rem] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              Answers {founderCount} / 10
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            disabled={founderCount === 0 || phase === "scoring" || phase === "thinking" || phase === "recording"}
            onClick={score}
          >
            <ClipboardCheck /> <span className="hidden sm:inline">End &</span> score
          </Button>
        </div>
      </header>

      <main className="mx-auto w-full max-w-3xl flex-1 space-y-4 px-4 py-6">
        {turns.map((t, i) =>
          t.role === "grant" ? (
            <div key={i} className="flex gap-3">
              <GrantAvatar className="h-9 w-9" speaking={phase === "speaking" && i === turns.length - 1} />
              <div className="max-w-[85%]">
                <p className="text-[0.7rem] font-semibold uppercase tracking-[0.14em] text-primary">Grant</p>
                <p className="mt-1 leading-relaxed">{t.text}</p>
              </div>
            </div>
          ) : (
            <div key={i} className="flex justify-end">
              <div className="max-w-[85%] rounded-lg bg-secondary px-4 py-3 text-secondary-foreground">
                <p className="text-[0.7rem] font-semibold uppercase tracking-[0.14em] text-muted-foreground">You</p>
                <p className="mt-1 leading-relaxed">{t.text}</p>
              </div>
            </div>
          ),
        )}
        {(phase === "thinking" || phase === "scoring") && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> {statusLabel[phase]}…
          </div>
        )}
        <div ref={bottom} />
      </main>

      <div className="sticky bottom-0 border-t border-border bg-background/95 backdrop-blur">
        <div className="mx-auto flex max-w-3xl flex-col items-center gap-3 px-4 py-5">
          <p className={cn("text-[0.7rem] font-semibold uppercase tracking-[0.14em]", phase === "recording" ? "text-destructive" : "text-muted-foreground")}>
            {statusLabel[phase]}
            {phase === "recording" && ` · ${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`}
          </p>
          {phase === "ready" && (
            <Button size="lg" onClick={() => say(turns[turns.length - 1]?.text ?? "")}>
              <Play /> Start the meeting
            </Button>
          )}
          {phase === "speaking" && (
            <Button variant="ghost" size="sm" onClick={skipAudio}>
              Skip audio
            </Button>
          )}
          {phase === "listening" && (
            <button
              onClick={startRecording}
              className="grid h-20 w-20 place-items-center rounded-full bg-primary text-primary-foreground shadow-lg transition hover:scale-105"
              aria-label="Start recording"
            >
              <Mic className="h-8 w-8" />
            </button>
          )}
          {phase === "recording" && (
            <button
              onClick={stopRecording}
              className="relative grid h-20 w-20 place-items-center rounded-full bg-destructive text-destructive-foreground"
              aria-label="Stop and send"
            >
              <span className="absolute inset-0 animate-ping rounded-full bg-destructive/40" />
              <Square className="relative h-7 w-7 fill-current" />
            </button>
          )}
          {(phase === "listening" || phase === "recording") && (
            <p className="text-xs text-muted-foreground">
              {phase === "listening" ? "Tap the mic, speak, then tap again to send." : "Tap to send your answer."}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
